import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';

import { MessageCenterService } from '@cigna/chcp/shared/message-center-data-access';
import { LatestUpdatesService } from '../latest-updates/latest-updates.service';
// TODO: https://git.sys.cigna.com/angular-guild/cigna-nx/issues/567
// TODO: resolve CHCP disables by bot
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import {
  ManageUserAccessService,
  User,
  UserEntitlmentResponse,
} from '@cigna/chcp/working-with-cigna/manage-access-feature';
import {
  loadLatestUpdatesSuccess,
  loadLatestUpdates,
  loadLatestUpdatesError,
  loadManageAccessUsers,
  loadManageAccessUsersSuccess,
  loadManageAccessUsersError,
  loadManageAccessUserDetail,
  loadManageAccessUserDetailSuccess,
  loadManageAccessUserDetailError,
  fetchMessages,
  fetchMessagesSuccess,
  fetchMessagesError,
  updateMessages,
  updateMessagesSuccess,
  updateMessagesError,
  deleteMessages,
  deleteMessagesSuccess,
  deleteMessagesError,
} from './dashboard.actions';
import { MessagesResponse } from '@cigna/chcp/shared/util';

@Injectable()
export class DashboardEffects {
  getLatestUpdates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadLatestUpdates),
      switchMap(() =>
        this.latestUpdateService.getLatestUpdates().pipe(
          map((data: any) => loadLatestUpdatesSuccess({ updates: data })),
          catchError((err) => of(loadLatestUpdatesError())),
        ),
      ),
    ),
  );

  getManageAccessUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadManageAccessUsers),
      switchMap(() =>
        this.manageAccessService.getPendingUsers().pipe(
          map((data: any) => loadManageAccessUsersSuccess({ users: data })),
          catchError((err) => of(loadManageAccessUsersError())),
        ),
      ),
    ),
  );

  getManageAccessUserDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadManageAccessUserDetail),
      switchMap(({ user }) =>
        this.manageAccessService.getUserTinEntitlements(user.id).pipe(
          map((data: any) => {
            const updatedUser = this.mapEntitlementsToUser(user, data[0]);
            return loadManageAccessUserDetailSuccess({ user: updatedUser });
          }),
          catchError((err) => of(loadManageAccessUserDetailError())),
        ),
      ),
    ),
  );

  fetchMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchMessages),
      switchMap(() =>
        this.messageCenterService
          .fetchMessages({
            messagesType: 'inbox',
          })
          .pipe(
            switchMap((res: MessagesResponse) =>
              of(fetchMessagesSuccess({ messages: res.messages })),
            ),
            catchError((error) => of(fetchMessagesError({ error }))),
          ),
      ),
    ),
  );

  updateMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateMessages),
      switchMap(({ update }) =>
        this.messageCenterService
          .updateMessages({ type: 'inbox', ...update })
          .pipe(
            switchMap(() => of(updateMessagesSuccess({ update }))),
            catchError((error) => of(updateMessagesError({ error }))),
          ),
      ),
    ),
  );

  deleteMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteMessages),
      switchMap(({ ids }) =>
        this.messageCenterService.deleteMessages(ids).pipe(
          switchMap(() => of(deleteMessagesSuccess({ ids }))),
          catchError((error) => of(deleteMessagesError({ error }))),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private latestUpdateService: LatestUpdatesService,
    private manageAccessService: ManageUserAccessService,
    private messageCenterService: MessageCenterService,
  ) {}

  mapEntitlementsToUser(
    user: User,
    entitlements: UserEntitlmentResponse | undefined,
  ): User {
    if (!entitlements) {
      return {
        ...user,
        tins: user.tins.map((tin) => ({
          ...tin,
          entitlements: [],
        })),
      };
    }
    return {
      ...user,
      tins: user.tins.map((tin) => {
        const matchedTin = entitlements.tins.find((t) => t.tin === tin.tin);

        if (matchedTin !== undefined && matchedTin.entitlements) {
          return {
            ...tin,
            entitlements: matchedTin.entitlements,
          };
        }
        return {
          ...tin,
          entitlements: [],
        };
      }),
    };
  }
}
